/**
 * Minna — The Ethereal Observer.
 * Long black fur, bright green glowing eyes.
 * Extends PlayerEntity with Minna-specific green eye light configuration.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3, Matrix } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { PLAYER, COLORS } from "../../config/GameConfig";
import { tagAs } from "../../levels/PlaceholderTag";
import { minnaAnimClips } from "./catAnimClips";
import { PlayerEntity, PlayerConfig } from "./PlayerEntity";
import { computeEyePlacements, MINNA_EYE } from "./characterAccents";

const MINNA_MODEL_SCALE = 0.78;

const MINNA_CONFIG: PlayerConfig = {
  name: "minna",
  // minna_stylized_whiskers.glb rigged and animated by
  // tools/blender/rig_cat.py (idle/walk/run/jump/fall/land/crouch; the
  // pipeline also moves her whiskers onto her muzzle). The CatAnimator in
  // PlayerEntity drives the clips.
  modelPath: "/assets/models/minna_anim.glb",
  modelScale: MINNA_MODEL_SCALE,
  animClips: minnaAnimClips(MINNA_MODEL_SCALE),
  mass: 1.0,
  capsuleRadius: PLAYER.CAPSULE_RADIUS,
  capsuleHeight: PLAYER.CAPSULE_HEIGHT,
  airAccelerationScale: 1.0,
  // Task 8: Minna feels snappier via higher ground acceleration/deceleration.
  // moveSpeed stays at the PLAYER default (5). jumpImpulse is set here (not
  // PLAYER.JUMP_IMPULSE, which Overclocked Leap reads): 9 → 9.3 gains ~2.8u
  // instead of 2.62u, giving L1's 2.5u p3_5→p3_6 step real margin while the
  // 3.0u+ ledges stay out of reach (tools/playtest tight-jumps.mjs).
  jumpImpulse: 9.3,
  acceleration: 40,          // 25 → 40: snappier ground ramp-up
  deceleration: 50,          // 35 → 50: crisper stops
  lightColor: Color3.FromHexString(COLORS.MINNA_AURA),
  lightSpecular: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
  lightIntensity: 1.0,
  lightRange: 5,
  lightOffset: new Vector3(0, PLAYER.CAPSULE_HEIGHT * 0.35, 0.2),
  furTint: new Color3(0.08, 0.07, 0.12),
  footstepStrideDistance: 0.6,
};

export class Minna extends PlayerEntity {
  /** Subtle green rim-light — makes Minna's silhouette visible on dark levels. */
  private rimLight: PointLight;

  /** Q-038 procedural glowing eyes (the GLB bakes eyes into the body texture). */
  private eyeMeshes: Mesh[] = [];
  private eyeMaterial: StandardMaterial | null = null;

  constructor(scene: Scene, spawnPosition: Vector3) {
    super(scene, spawnPosition, MINNA_CONFIG);

    // Canonical "bright green glowing eyes" — a faint glow cast from eye height.
    this.rimLight = new PointLight("minnaRimLight", Vector3.Zero(), scene);
    this.rimLight.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
    this.rimLight.specular = Color3.FromHexString(COLORS.MINNA_AURA);
    // 0.4/2.5 left Minna as an unreadable black cutout against dark
    // backdrops in every level (2026-07-10 visual review) — her long
    // black fur needs the eye-glow to actually rim-light her.
    this.rimLight.intensity = 0.45;
    this.rimLight.range = 4.0;
    this.rimLight.parent = this.mesh;
    // Offset slightly forward and up to eye level so the glow reads as eye-cast.
    this.rimLight.position = new Vector3(0, PLAYER.CAPSULE_HEIGHT * 0.42, 0.35);
  }

  /**
   * Q-038: canonical "bright green glowing eyes" as procedural geometry.
   * minna_stylized_whiskers.glb is a single merged mesh with the eyes baked
   * into the body texture — there is no eye material to make emissive — so
   * two small unlit emissive spheres are parented to the model root. Root
   * parenting means they flip with facing (root.rotation.y), inherit the
   * squash/stretch pose, and inherit ShadowSlip's stealth fade (its
   * getChildMeshes(false) visibility loop covers all capsule descendants).
   *
   * Deliberately NO new lights (L1 fps budget) and NO addIncludedOnlyMesh
   * (one call would flip the shared GlowLayer into include-only mode and
   * silence every crystal/portal glow in the level).
   */
  protected override onModelLoaded(): void {
    const root = this.modelRoot;
    if (!root) return;
    this.excludeModelFromLight(this.rimLight);
    // The generated coat's metallic map is almost white (mean B=253/255).
    // Fur is dielectric: treating it as metal removes diffuse light and
    // makes this black cat a cutout in scenes without an environment map.
    // Keep the authored albedo/normal detail and the separate whiskers.
    for (const mesh of root.getChildMeshes()) {
      const material = mesh.material;
      if (material instanceof PBRMaterial && material.albedoTexture) {
        material.metallic = 0;
        material.roughness = 0.85;
      }
    }
    // Re-measure AFTER floor alignment moved the root. Whiskers are left
    // out of the box: they stick out of the head and were once exported
    // behind the tail, and neither should move the eyes.
    root.computeWorldMatrix(true);
    const bb = root.getHierarchyBoundingVectors(
      true,
      (m) => !/whisker/i.test(m.material?.name ?? ""),
    );
    // Rigged model: ride on the Head bone so the eyes follow head motion.
    // Otherwise the model root (static mesh, procedural pose).
    const head = root
      .getDescendants(false)
      .find((n): n is TransformNode => n instanceof TransformNode && /(^|\W)Head$/.test(n.name));
    const anchor: TransformNode = head ?? root;
    anchor.computeWorldMatrix(true);
    const invAnchor = Matrix.Invert(anchor.getWorldMatrix());
    // Eye size is in world units; the sphere lives in the anchor's space,
    // so undo the anchor's (possibly non-uniform) world scale per axis.
    const anchorScale = new Vector3(1, 1, 1);
    anchor.getWorldMatrix().decompose(anchorScale);

    const mat = new StandardMaterial("minnaEyeGlowMat", this.scene);
    mat.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
    mat.diffuseColor = Color3.Black();
    mat.specularColor = Color3.Black();
    // Unlit: reads as glowing at gameplay zoom even in low-fi mode (glow
    // layer disabled) and needs NO GlowLayer registration — default-include
    // picks it up.
    mat.disableLighting = true;
    this.eyeMaterial = mat;

    for (const [i, world] of computeEyePlacements(bb).entries()) {
      const eye = MeshBuilder.CreateSphere(
        `minnaEye_${i === 0 ? "L" : "R"}`,
        { diameter: MINNA_EYE.DIAMETER, segments: 8 },
        this.scene,
      );
      eye.material = mat;
      eye.isPickable = false;
      eye.parent = anchor;
      eye.scaling.set(1 / anchorScale.x, 1 / anchorScale.y, 1 / anchorScale.z);
      // Load-time only — the Vector3 allocation here is not per-frame.
      Vector3.TransformCoordinatesToRef(
        new Vector3(world.x, world.y, world.z),
        invAnchor,
        eye.position,
      );
      tagAs(eye, "final");
      this.eyeMeshes.push(eye);
    }
  }

  dispose(): void {
    this.rimLight.dispose();
    // Dispose eyes before super.dispose(): the model-instance dispose would
    // catch the parented meshes, but the material is scene-owned and would
    // otherwise leak across level reloads.
    for (const eye of this.eyeMeshes) {
      eye.dispose();
    }
    this.eyeMeshes = [];
    if (this.eyeMaterial) {
      this.eyeMaterial.dispose();
      this.eyeMaterial = null;
    }
    super.dispose();
  }
}
